import { useEffect, useRef, useState } from 'react'
import { documents, photoEssay, pressClippings, requests, sourceLinks } from './data.js'
import { englishDocuments, englishPhotoEssay, englishPressTitles, englishRequests, ui } from './i18n.js'

const initialPreferences = {
  highContrast: false,
  largeText: false,
  reducedMotion: false,
}

function Icon({ name, size = 22 }) {
  const paths = {
    arrow: <><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></>,
    back: <><path d="M19 12H5"/><path d="m11 6-6 6 6 6"/></>,
    play: <path d="m9 7 8 5-8 5Z" fill="currentColor" stroke="none"/>,
    file: <><path d="M6 3h8l4 4v14H6z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h6"/></>,
    download: <><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></>,
    external: <><path d="M14 5h5v5"/><path d="m11 13 8-8"/><path d="M19 13v6H5V5h6"/></>,
    accessibility: <><circle cx="12" cy="4.5" r="2"/><path d="M5 8h14M12 7v14M8 21l4-7 4 7"/></>,
    close: <><path d="m6 6 12 12M18 6 6 18"/></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16"/></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="1"/><path d="M8 3v4M16 3v4M3 10h18"/></>,
    zoom: <><circle cx="11" cy="11" r="6"/><path d="m16 16 4 4M11 8v6M8 11h6"/></>,
  }
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  )
}

function Wordmark({ inverse = false, label = ui.it.wordmarkLabel }) {
  return (
    <a className={`wordmark ${inverse ? 'wordmark--inverse' : ''}`} href="#inizio" aria-label={label}>
      <span className="wordmark__title">Arengo</span>
      <i aria-hidden="true" />
      <span className="wordmark__subtitle">Memoria viva</span>
    </a>
  )
}

function Header({ preferences, setPreferences, language, setLanguage, t }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [accessOpen, setAccessOpen] = useState(false)
  const accessTriggerRef = useRef(null)
  const accessPanelRef = useRef(null)

  useEffect(() => {
    if (!accessOpen) return undefined
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setAccessOpen(false)
      if (event.key === 'Tab') {
        const focusable = [...accessPanelRef.current.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      accessTriggerRef.current?.focus()
    }
  }, [accessOpen])

  const toggle = (key) => {
    setPreferences((current) => ({ ...current, [key]: !current[key] }))
  }

  const closeMenu = () => setMenuOpen(false)

  return (
    <header className="site-header">
      <Wordmark label={t.wordmarkLabel} />
      <button className="menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="primary-nav" onClick={() => setMenuOpen((open) => !open)}>
        <span className="sr-only">{t.menuToggle}</span>
        <Icon name={menuOpen ? 'close' : 'menu'} />
      </button>
      <nav id="primary-nav" className={`primary-nav ${menuOpen ? 'is-open' : ''}`} aria-label={t.navLabel}>
        <a href="#storia" onClick={closeMenu}>{t.nav[0]}</a>
        <a href="#fotografie" onClick={closeMenu}>{t.nav[1]}</a>
        <a href="#richieste" onClick={closeMenu}>{t.nav[2]}</a>
        <a href="#cammino" onClick={closeMenu}>{t.nav[3]}</a>
        <a href="#archivio" onClick={closeMenu}>{t.nav[4]}</a>
        <div className="language-switch" role="group" aria-label={t.languageLabel}>
          <button type="button" aria-pressed={language === 'it'} onClick={() => setLanguage('it')} lang="it">IT</button>
          <span aria-hidden="true">/</span>
          <button type="button" aria-pressed={language === 'en'} onClick={() => setLanguage('en')} lang="en">EN</button>
        </div>
        <button ref={accessTriggerRef} type="button" className="access-trigger" onClick={() => { setAccessOpen(true); closeMenu() }}>
          <Icon name="accessibility" />
          <span>{t.accessibility}</span>
        </button>
      </nav>
      {accessOpen ? (
        <div className="access-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setAccessOpen(false) }}>
          <aside ref={accessPanelRef} className="access-panel" role="dialog" aria-modal="true" aria-labelledby="access-title">
            <div className="access-panel__heading">
              <div>
                <p className="section-label">{t.readingPreferences}</p>
                <h2 id="access-title">{t.accessibility}</h2>
              </div>
              <button className="icon-button" type="button" aria-label={t.closeAccessibility} onClick={() => setAccessOpen(false)} autoFocus>
                <Icon name="close" />
              </button>
            </div>
            <p>{t.accessibilityIntro}</p>
            <div className="access-options">
              <button type="button" aria-pressed={preferences.highContrast} onClick={() => toggle('highContrast')}>
                <span>{t.highContrast}</span><span>{preferences.highContrast ? t.active : t.inactive}</span>
              </button>
              <button type="button" aria-pressed={preferences.largeText} onClick={() => toggle('largeText')}>
                <span>{t.largeText}</span><span>{preferences.largeText ? t.active : t.inactive}</span>
              </button>
              <button type="button" aria-pressed={preferences.reducedMotion} onClick={() => toggle('reducedMotion')}>
                <span>{t.reducedMotion}</span><span>{preferences.reducedMotion ? t.active : t.inactive}</span>
              </button>
            </div>
            <button className="text-button" type="button" onClick={() => setPreferences(initialPreferences)}>{t.reset}</button>
          </aside>
        </div>
      ) : null}
    </header>
  )
}

function Hero({ t }) {
  return (
    <section id="inizio" className="hero section-stone" aria-labelledby="hero-title">
      <HeaderSlot />
      <div className="hero__content">
        <div className="hero__copy">
          <p className="hero__date-line">{t.heroDate} <span aria-hidden="true">·</span> San Marino</p>
          <h1 id="hero-title">{t.heroTitleStart}<br />{t.heroTitleMiddle} <em>{t.heroTitleEnd}</em></h1>
          <p className="hero__lead">{t.heroLead}</p>
          <div className="hero__actions">
            <a className="button button--primary" href="#storia">{t.enterStory} <Icon name="arrow" /></a>
            <a className="button button--ghost" href="#film"><Icon name="play" /> {t.watchVideo}</a>
          </div>
        </div>
        <figure className="hero__figure">
          <img src="/assets/immagini/hero-arengo-2021.webp" alt={t.heroAlt} width="2200" height="1469" fetchPriority="high" />
          <figcaption>{t.heroCaption}</figcaption>
        </figure>
      </div>
      <a className="scroll-cue" href="#storia"><span>{t.scroll}</span><Icon name="arrow" /></a>
    </section>
  )
}

function HeaderSlot() {
  return <div className="header-slot" aria-hidden="true" />
}

function History({ t }) {
  return (
    <section id="storia" className="history section-stone" aria-labelledby="history-title">
      <div className="history__grid">
        <div className="history__copy">
          <p className="chapter-stamp"><span>01</span> {t.chapterMemory}</p>
          <p className="section-label">{t.memory}</p>
          <h2 id="history-title">{t.historyTitle}</h2>
          <p className="lead">{t.historyLead}</p>
          <div className="history__why">
            <h3>{t.whyArengo}</h3>
            <p>{t.whyArengoText}</p>
          </div>
        </div>
        <aside className="pandemic-note" aria-label={t.pandemicLabel}>
          <p className="section-label">{t.pandemicLabel}</p>
          <q>{t.pandemicQuote}</q>
          <p>{t.pandemicText}</p>
          <small>{t.pandemicNote}</small>
        </aside>
        <div className="historic-numbers" aria-label={t.historicNumbersLabel}>
          <div><strong>805</strong><span>{t.households}</span></div>
          <span className="historic-numbers__bridge">1906 <i aria-hidden="true">↔</i> 2021</span>
          <div><strong>727</strong><span>{t.favourable}</span></div>
        </div>
      </div>
    </section>
  )
}

function PhotoEssayButton({ index, className = '', onOpen, photos, t }) {
  const item = photos[index]
  return (
    <button className={`photo-essay__photo ${className}`} type="button" onClick={(event) => onOpen(event, index)} aria-label={`${t.enlargePhoto}: ${item.title}`}>
      <figure>
        <img src={item.src} alt={item.alt} width={item.width} height={item.height} loading="lazy" decoding="async" />
        <span className="photo-essay__zoom" aria-hidden="true"><Icon name="zoom" /></span>
        <figcaption>{item.title}</figcaption>
      </figure>
    </button>
  )
}

function PhotoEssay({ photos, t }) {
  const [selected, setSelected] = useState(null)
  const openerRef = useRef(null)
  const dialogRef = useRef(null)
  const lightboxOpen = selected !== null
  const featured = [0, 1, 4, 5]

  useEffect(() => {
    if (!lightboxOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSelected(null)
      if (event.key === 'ArrowLeft') setSelected((current) => (current - 1 + photos.length) % photos.length)
      if (event.key === 'ArrowRight') setSelected((current) => (current + 1) % photos.length)
      if (event.key === 'Tab') {
        const focusable = [...dialogRef.current.querySelectorAll('button, [tabindex]:not([tabindex="-1"])')]
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
      openerRef.current?.focus()
    }
  }, [lightboxOpen, photos.length])

  const openPhoto = (event, index) => {
    openerRef.current = event.currentTarget
    setSelected(index)
  }
  const photo = selected === null ? null : photos[selected]
  const photoTitleLines = t.photoTitle.split('\n')

  return (
    <section id="fotografie" className="photo-essay section-stone" aria-labelledby="photo-essay-title">
      <div className="photo-essay__top">
        <div className="photo-essay__intro">
          <p className="photo-essay__date">{t.photoDate} <span aria-hidden="true">·</span> Piazzale Domus Plebis</p>
          <h2 id="photo-essay-title">{photoTitleLines[0]}<br />{photoTitleLines[1]}</h2>
          <p className="photo-essay__lead">{t.photoLead}</p>
          <blockquote>
            <p>{t.photoQuote}</p>
            <cite>{t.photoQuoteSource}</cite>
          </blockquote>
          <button className="button button--ink" type="button" onClick={(event) => openPhoto(event, 0)}>{t.explorePhotos} <Icon name="arrow" /></button>
        </div>
        <PhotoEssayButton index={featured[0]} className="photo-essay__photo--featured" onOpen={openPhoto} photos={photos} t={t} />
      </div>
      <div className="photo-essay__strip">
        <PhotoEssayButton index={featured[1]} onOpen={openPhoto} photos={photos} t={t} />
        <PhotoEssayButton index={featured[2]} onOpen={openPhoto} photos={photos} t={t} />
        <PhotoEssayButton index={featured[3]} onOpen={openPhoto} photos={photos} t={t} />
      </div>
      <p className="photo-essay__archive-note">{t.photoArchiveNote}</p>

      {photo ? (
        <div className="photo-lightbox" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setSelected(null) }}>
          <div
            ref={dialogRef}
            className={`photo-lightbox__dialog ${photo.height > photo.width ? 'photo-lightbox__dialog--portrait' : 'photo-lightbox__dialog--landscape'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="photo-lightbox-title"
          >
            <div className="photo-lightbox__bar">
              <span>{String(selected + 1).padStart(2, '0')} / {String(photos.length).padStart(2, '0')}</span>
              <button type="button" className="icon-button icon-button--inverse" aria-label={t.closePhotos} onClick={() => setSelected(null)} autoFocus><Icon name="close" /></button>
            </div>
            <button className="photo-lightbox__nav photo-lightbox__nav--previous" type="button" aria-label={t.previousPhoto} onClick={() => setSelected((selected - 1 + photos.length) % photos.length)}><Icon name="back" /></button>
            <figure><img src={photo.src} alt={photo.alt} width={photo.width} height={photo.height} /></figure>
            <div className="photo-lightbox__info">
              <p className="section-label">{t.photoArchive}</p>
              <h3 id="photo-lightbox-title">{photo.title}</h3>
              <p>{photo.date} · Piazzale Domus Plebis</p>
            </div>
            <button className="photo-lightbox__nav photo-lightbox__nav--next" type="button" aria-label={t.nextPhoto} onClick={() => setSelected((selected + 1) % photos.length)}><Icon name="arrow" /></button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function RequestsExplorer({ requestItems, t }) {
  const [selected, setSelected] = useState(0)
  const item = requestItems[selected]

  const move = (direction) => {
    setSelected((current) => (current + direction + requestItems.length) % requestItems.length)
  }

  return (
    <section id="richieste" className="requests-section section-dark" aria-labelledby="requests-title">
      <div className="section-heading section-heading--wide">
        <p className="section-label">{t.documentLabel}</p>
        <h2 id="requests-title">{t.requestsTitle}</h2>
        <p>{t.requestsLead}</p>
      </div>
      <div className="requests-explorer">
        <ol className="requests-index" aria-label={t.requestsIndex}>
          {requestItems.map((request, index) => (
            <li key={request.short}>
              <button type="button" aria-current={selected === index ? 'true' : undefined} onClick={() => setSelected(index)}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <b>{request.short}</b>
                {selected === index ? <Icon name="arrow" size={19} /> : null}
              </button>
            </li>
          ))}
        </ol>
        <article className="request-detail" aria-live="polite" aria-atomic="true">
          <div className="request-detail__nav">
            <button type="button" onClick={() => move(-1)} aria-label={t.previousRequest}><Icon name="back" /> {t.previous}</button>
            <span>{selected + 1} {t.of} {requestItems.length}</span>
            <button type="button" onClick={() => move(1)} aria-label={t.nextRequest}>{t.next} <Icon name="arrow" /></button>
          </div>
          <p className="request-detail__number">{String(selected + 1).padStart(2, '0')}</p>
          <h3>{String(selected + 1).padStart(2, '0')} — {item.title}</h3>
          <p>{item.text}</p>
          <blockquote>{t.motto}</blockquote>
          <div className="request-progress" aria-hidden="true"><span style={{ width: `${((selected + 1) / requestItems.length) * 100}%` }} /></div>
          <a className="button button--outline-gold" href="/documenti/documento-arengo-12-richieste.pdf" target="_blank" rel="noreferrer"><Icon name="file" /> {t.readOriginal}</a>
        </article>
        <figure className="request-visual">
          <img src="/assets/fotografie/stele-bandiera-2021.webp" alt={t.requestAlt} width="2200" height="3296" loading="lazy" />
          <figcaption>{t.requestCaption}</figcaption>
        </figure>
      </div>
    </section>
  )
}

function Timeline({ t }) {
  const timelineTitle = t.timelineTitle.split('\n')
  return (
    <section id="cammino" className="timeline section-stone" aria-labelledby="timeline-title">
      <div className="section-heading">
        <p className="section-label">{t.timelineLabel}</p>
        <h2 id="timeline-title">{timelineTitle[0]}<br />{timelineTitle[1]}</h2>
      </div>
      <div className="timeline-list">
        <article className="timeline-item">
          <div className="timeline-item__marker"><span>2021</span></div>
          <div className="timeline-item__copy"><h3>{t.timeline2021Title}</h3><p>{t.timeline2021Text}</p></div>
          <figure><img src="/assets/immagini/hero-arengo-2021.webp" alt={t.timeline2021Alt} width="2200" height="1469" loading="lazy" /></figure>
        </article>
        <article className="timeline-item">
          <div className="timeline-item__marker"><span>2023</span></div>
          <div className="timeline-item__copy"><h3>{t.timeline2023Title}</h3><p>{t.timeline2023Text}</p></div>
          <figure><img src="/assets/fotografie/svelamento-pioggia-2023.webp" alt={t.timeline2023Alt} width="2200" height="1469" loading="lazy" /></figure>
        </article>
        <article className="timeline-item timeline-item--night">
          <div className="timeline-item__marker"><span>2026</span></div>
          <div className="timeline-item__copy"><h3>{t.timeline2026Title}</h3><p>{t.timeline2026Text}</p><a className="text-link" href={sourceLinks.article} target="_blank" rel="noreferrer">{t.timeline2026Link} <Icon name="arrow" /></a></div>
          <figure><img src="/assets/immagini/stele-illuminata-2026.webp" alt={t.timeline2026Alt} width="757" height="800" loading="lazy" /></figure>
        </article>
      </div>
    </section>
  )
}

function DocumentRow({ document, language, t }) {
  return (
    <li>
      <a href={document.href} download>
        <span className={`file-type file-type--${document.type.toLowerCase()}`}>{document.type}</span>
        <span>{document.label} <em>· {document.type}{language === 'en' ? ` · ${t.originalItalian}` : ''}</em></span>
        <Icon name="download" />
      </a>
    </li>
  )
}

function PressGallery({ clippings, t }) {
  const [selected, setSelected] = useState(null)
  const openerRef = useRef(null)
  const dialogRef = useRef(null)
  const lightboxOpen = selected !== null

  useEffect(() => {
    if (!lightboxOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSelected(null)
      if (event.key === 'ArrowLeft') setSelected((current) => (current - 1 + clippings.length) % clippings.length)
      if (event.key === 'ArrowRight') setSelected((current) => (current + 1) % clippings.length)
      if (event.key === 'Tab') {
        const focusable = [...dialogRef.current.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')]
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
      openerRef.current?.focus()
    }
  }, [clippings.length, lightboxOpen])

  const clipping = selected === null ? null : clippings[selected]

  return (
    <section className="press-gallery" aria-labelledby="press-gallery-title">
      <div className="press-gallery__intro">
        <div>
          <p className="section-label">{t.pressLabel}</p>
          <h3 id="press-gallery-title">{t.pressTitle}</h3>
        </div>
        <p>{t.pressLead}</p>
      </div>
      <ol className="press-gallery__grid">
        {clippings.map((clipping, index) => (
          <li className="press-card" key={clipping.src}>
            <button type="button" onClick={(event) => { openerRef.current = event.currentTarget; setSelected(index) }} aria-label={`${t.openEnlarge}: ${clipping.title}`}>
              <figure>
                <div className="press-card__image">
                  <img src={clipping.src} alt={clipping.alt} width={clipping.width} height={clipping.height} loading="lazy" decoding="async" />
                  <span className="press-card__number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <figcaption>
                  <span>{clipping.source}</span>
                  <strong>{clipping.title}</strong>
                  <small>{clipping.date}</small>
                  <em>{t.explorePage} <Icon name="arrow" size={18} /></em>
                </figcaption>
              </figure>
            </button>
          </li>
        ))}
      </ol>
      {clipping ? (
        <div className="press-lightbox" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setSelected(null) }}>
          <div ref={dialogRef} className="press-lightbox__dialog" role="dialog" aria-modal="true" aria-labelledby="press-lightbox-title">
            <div className="press-lightbox__bar">
              <span>{String(selected + 1).padStart(2, '0')} / {String(clippings.length).padStart(2, '0')}</span>
              <button type="button" className="icon-button icon-button--inverse" aria-label={t.closePress} onClick={() => setSelected(null)} autoFocus><Icon name="close" /></button>
            </div>
            <button className="press-lightbox__nav press-lightbox__nav--previous" type="button" aria-label={t.previousPage} onClick={() => setSelected((selected - 1 + clippings.length) % clippings.length)}><Icon name="back" /></button>
            <figure>
              <img src={clipping.src} alt={clipping.alt} width={clipping.width} height={clipping.height} />
            </figure>
            <div className="press-lightbox__info">
              <p className="section-label">{clipping.source}</p>
              <h4 id="press-lightbox-title">{clipping.title}</h4>
              <p>{clipping.date}</p>
              <a className="button button--outline-gold" href={clipping.src} target="_blank" rel="noreferrer">{t.openOriginal} <Icon name="external" /></a>
            </div>
            <button className="press-lightbox__nav press-lightbox__nav--next" type="button" aria-label={t.nextPage} onClick={() => setSelected((selected + 1) % clippings.length)}><Icon name="arrow" /></button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function Archive({ documentItems, clippings, language, t }) {
  return (
    <section id="archivio" className="archive section-stone" aria-labelledby="archive-title">
      <div className="section-heading section-heading--wide">
        <p className="section-label">{t.archiveLabel}</p>
        <h2 id="archive-title">{t.archiveTitle}</h2>
        <p>{t.archiveLead}</p>
      </div>
      <div className="archive-grid">
        <div className="archive-main">
          <article id="film" className="film-block">
            <video controls preload="metadata" poster="/assets/immagini/hero-arengo-2021.webp">
              <source src={sourceLinks.video} type="video/mp4" />
              {t.videoFallback}
            </video>
            <div className="film-block__info">
              <div><p className="section-label">{t.videoLabel}</p><h3>{t.videoTitle}</h3></div>
            </div>
          </article>
          <details className="transcript">
            <summary><Icon name="file" /> {t.transcriptTitle} <Icon name="arrow" /></summary>
            <div>
              <p>{t.transcript1}</p>
              <p>{t.transcript2}</p>
            </div>
          </details>
          <div className="documents-block">
            <h3>{t.originalDocuments}</h3>
            <ul>{documentItems.map((document) => <DocumentRow key={document.href} document={document} language={language} t={t} />)}</ul>
          </div>
        </div>
        <aside className="press-block" aria-labelledby="press-title">
          <p className="section-label">{t.pressArchiveLabel}</p>
          <h3 id="press-title">{t.pressArchiveTitle}</h3>
          <a href="/documenti/rassegna-stampa-serenissima-22-febbraio-2021.pdf" target="_blank" rel="noreferrer">
            <img src="/assets/immagini/stampa-22-febbraio-2021.webp" alt={t.pressImageAlt} loading="lazy" />
            <span>{t.pressPdf} <Icon name="arrow" /></span>
          </a>
        </aside>
      </div>
      <PressGallery clippings={clippings} t={t} />
    </section>
  )
}

function Closing({ t }) {
  return (
    <section className="closing section-dark" aria-labelledby="closing-title">
      <div className="closing__copy">
        <p className="section-label">{t.closingLabel}</p>
        <h2 id="closing-title">{t.closingTitle}</h2>
        <p className="closing__lead">{t.closingLead}</p>
        <div className="light-line" aria-hidden="true"><span /></div>
        <p>{t.closingLine}</p>
        <div className="closing__links">
          <a className="text-link" href={sourceLinks.official} target="_blank" rel="noreferrer">{t.discover} <Icon name="arrow" /></a>
          <a className="text-link" href="#inizio">{t.backTop} <Icon name="arrow" /></a>
        </div>
        <p className="thanks">{t.thanks}</p>
      </div>
      <figure className="closing__figure">
        <img src="/assets/fotografie/arengo-striscione-basilica-2021.webp" alt={t.closingAlt} width="2200" height="1469" loading="lazy" />
        <figcaption>{t.closingCaption}</figcaption>
      </figure>
      <blockquote className="closing__quote">{t.closingQuote}</blockquote>
    </section>
  )
}

function Footer({ t }) {
  return (
    <footer className="site-footer">
      <Wordmark inverse label={t.wordmarkLabel} />
      <p>{t.footerText}</p>
      <nav aria-label={t.footerNav}>
        <a href={sourceLinks.official} target="_blank" rel="noreferrer">{t.officialSite}</a>
        <a href={sourceLinks.project} target="_blank" rel="noreferrer">{t.arengoPage}</a>
        <a href={sourceLinks.article} target="_blank" rel="noreferrer">{t.illuminatedStele}</a>
      </nav>
      <small>{t.credits}</small>
    </footer>
  )
}

function App() {
  const [language, setLanguage] = useState(() => localStorage.getItem('arengo-language') === 'en' ? 'en' : 'it')
  const [preferences, setPreferences] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('arengo-accessibility-v1'))
      return saved ? { ...initialPreferences, ...saved } : initialPreferences
    } catch {
      return initialPreferences
    }
  })

  useEffect(() => {
    localStorage.setItem('arengo-accessibility-v1', JSON.stringify(preferences))
  }, [preferences])

  useEffect(() => {
    localStorage.setItem('arengo-language', language)
    document.documentElement.lang = language
    document.title = ui[language].pageTitle
  }, [language])

  useEffect(() => {
    let frame = 0
    const updateProgress = () => {
      const available = document.documentElement.scrollHeight - window.innerHeight
      const progress = available > 0 ? Math.min(100, Math.max(0, (window.scrollY / available) * 100)) : 0
      document.documentElement.style.setProperty('--reading-progress', `${progress}%`)
      frame = 0
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateProgress)
    }
    updateProgress()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  useEffect(() => {
    const targets = document.querySelectorAll('.history__grid, .photo-essay__intro, .photo-essay__photo, .requests-explorer, .timeline-item, .archive-grid, .press-gallery__intro, .press-card, .closing__copy, .closing__figure, .closing__quote')
    document.documentElement.classList.add('reveal-ready')
    targets.forEach((target) => target.setAttribute('data-reveal', ''))
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-visible', '')
          observer.unobserve(entry.target)
        }
      })
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    targets.forEach((target) => observer.observe(target))
    return () => {
      observer.disconnect()
      document.documentElement.classList.remove('reveal-ready')
    }
  }, [])

  const classes = [
    'app',
    preferences.highContrast ? 'is-high-contrast' : '',
    preferences.largeText ? 'is-large-text' : '',
    preferences.reducedMotion ? 'is-reduced-motion' : '',
  ].filter(Boolean).join(' ')

  const t = ui[language]
  const requestItems = language === 'en' ? englishRequests : requests
  const documentItems = documents.map((document, index) => ({ ...document, label: language === 'en' ? englishDocuments[index] : document.label }))
  const photos = photoEssay.map((photo, index) => language === 'en'
    ? { ...photo, title: englishPhotoEssay[index][0], alt: englishPhotoEssay[index][1], date: '21 February 2021' }
    : photo)
  const clippings = pressClippings.map((clipping, index) => language === 'en'
    ? { ...clipping, title: englishPressTitles[index], date: clipping.date.replace('febbraio', 'February').replace('pagine', 'pages').replace('pagina', 'page').replace('Verso l’Arengo', 'Towards the Arengo') }
    : clipping)

  return (
    <div className={classes}>
      <a className="skip-link" href="#contenuto">{t.skip}</a>
      <div className="reading-progress" aria-hidden="true"><span /></div>
      <Header preferences={preferences} setPreferences={setPreferences} language={language} setLanguage={setLanguage} t={t} />
      <main id="contenuto">
        <Hero t={t} />
        <History t={t} />
        <PhotoEssay photos={photos} t={t} />
        <RequestsExplorer requestItems={requestItems} t={t} />
        <Timeline t={t} />
        <Archive documentItems={documentItems} clippings={clippings} language={language} t={t} />
        <Closing t={t} />
      </main>
      <Footer t={t} />
    </div>
  )
}

export default App
